import { NOT_OBSERVED, type ResponseValue } from '@genoma/shared';

import { useTranslate } from '../../i18n/index.tsx';

type Props = {
  value: ResponseValue | undefined;
  onChange: (value: ResponseValue) => void;
  /** Name of the person being placed, for the prompt and labels. */
  personName: string;
  /** True when the person being placed is the rater themselves. */
  isSelf?: boolean;
};

/**
 * The 1-5 selector of the individual format.
 *
 * The track fills toward the chosen step because the scale is a ladder, not a
 * set of options: the visual says "how far up", not "which box".
 */
export const ScaleSelector = ({ value, onChange, personName, isSelf = false }: Props) => {
  const t = useTranslate();
  const numeric = typeof value === 'number' ? value : null;
  const prompt = isSelf ? t('scale.promptSelf') : t('scale.prompt', { name: personName });

  const answerLabel =
    value === undefined
      ? null
      : value === NOT_OBSERVED
        ? t('scale.notObserved')
        : value === 2
          ? t('scale.transitionFrom', { from: t('scale.step1'), to: t('scale.step3') })
          : value === 4
            ? t('scale.transitionFrom', { from: t('scale.step3'), to: t('scale.step5') })
            : `${value} · ${t(`scale.step${value}`)}`;

  return (
    <>
      <p
        className={[
          'mx-0.5 mt-3.5 min-h-[19px] text-center text-[13px]',
          answerLabel ? 'font-semibold text-teal-deep' : 'text-faint italic',
        ].join(' ')}
        aria-live="polite"
      >
        {answerLabel ? (
          <>
            {t('scale.yourAnswer')}: <b>{answerLabel}</b>
          </>
        ) : (
          t('scale.placeholder')
        )}
      </p>

      <p className="mt-2 text-center font-mono text-[12.5px] tracking-[0.03em] text-faint uppercase">
        {prompt}
      </p>

      <div className="relative mx-2.5 mt-5">
        <div className="absolute top-5 right-[22px] left-[22px] h-[3px] rounded-full bg-line">
          <span
            className="block h-full rounded-full bg-teal transition-[width] duration-350 ease-out"
            style={{ width: numeric ? `${((numeric - 1) / 4) * 100}%` : '0%' }}
          />
        </div>

        <div
          className="relative flex justify-between"
          role="radiogroup"
          aria-label={prompt}
        >
          {[1, 2, 3, 4, 5].map((step) => (
            <button
              key={step}
              type="button"
              role="radio"
              aria-checked={numeric === step}
              aria-label={`${step}`}
              onClick={() => onChange(step as ResponseValue)}
              className="relative z-10 cursor-pointer border-none bg-transparent p-0"
            >
              <span
                className={[
                  'grid size-10 place-items-center rounded-full border-2 font-mono text-[15px] font-bold transition sm:size-11 sm:text-base',
                  numeric === step
                    ? 'scale-110 border-teal bg-teal text-white shadow-selected'
                    : 'border-line bg-white text-soft hover:border-teal hover:text-teal',
                ].join(' ')}
              >
                {step}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="mx-0.5 mt-2.5 flex justify-between font-mono text-[9.5px] tracking-[0.06em] text-faint uppercase">
        <span>← {t('scale.lowEnd')}</span>
        <span>{t('scale.highEnd')} →</span>
      </div>

      <button
        type="button"
        onClick={() => onChange(NOT_OBSERVED)}
        aria-pressed={value === NOT_OBSERVED}
        className={[
          'mt-4 flex w-full cursor-pointer items-center gap-2.5 rounded-xl border px-4 py-3 text-left transition',
          value === NOT_OBSERVED
            ? 'border-ink bg-white'
            : 'border-line bg-transparent hover:border-soft',
        ].join(' ')}
      >
        <span
          className={[
            'size-5 shrink-0 rounded-full border-2',
            value === NOT_OBSERVED ? 'border-ink bg-ink' : 'border-line',
          ].join(' ')}
        />
        <span>
          <b
            className={[
              'text-sm font-semibold',
              value === NOT_OBSERVED ? 'text-ink' : 'text-soft',
            ].join(' ')}
          >
            {t('scale.notObserved')}
          </b>{' '}
          <span className="text-[12.5px] text-faint">{t('scale.notObservedHint')}</span>
        </span>
      </button>
    </>
  );
};
